import { type ReactNode, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, KeyRound, PenLine, Settings, Mail, Sun, Moon, ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n/useTranslations';
import { useTheme } from '@/components/theme-provider';
import { useNavigation } from '@/hooks/use-navigation';
import { useLogout } from '@/hooks/use-logout';
import { useAuthStore } from '@/stores/auth-store';
import { useMessageStore } from '@/stores/message-store';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useShowTryGuidedButton } from '@/hooks/use-guided-ui-mode';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { UserRole } from '@comp-sys/shared';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { MobileSidebar } from '@/components/layout/mobile-sidebar';
import { BrandLogo } from '@/components/shared/brand-logo';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';
import { RoleSwitcherMenuItems } from '@/components/auth/role-switcher';
import { ChangePasswordDialog } from '@/components/users/change-password-dialog';
import { ChangeNameDialog } from '@/components/users/change-name-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Luxury 家族 shell（obsidian 主题）——「编辑 / 画廊」。
 *
 * 摒弃侧栏，采用高级刊物的横向编排：
 * - 顶部文字导航：wordmark + 大写字距文本菜单（激活项金色下划线），无图标侧栏
 * - 二级导航：当前栏目子项作为横向「章节」链
 * - 内容编辑式：大留白、衬线大标题、金色发丝线、全幅留白，如翻阅 premium 刊物
 * - 优雅过渡（淡入 + 轻缩放）
 *
 * 金色 = obsidian 的 primary token；深色优先。
 */
export function LuxuryShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const user = useAuthStore((state) => state.user);
  const { navItems } = useNavigation();
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';
  const t = useTranslations('nav');

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const isActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'));

  const activeTop = navItems.find((i) => isActive(i.href) && i.children?.length);
  const subNav = activeTop?.children ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      <LuxuryNav pathname={pathname} navItems={navItems} isActive={isActive} />

      {/* 二级章节链 */}
      {subNav.length > 0 && (
        <nav className="border-b border-primary/20 bg-background/60">
          <div className="mx-auto max-w-6xl flex items-center gap-6 px-4 h-10 overflow-x-auto">
            {subNav.map((child) => {
              const childActive = isActive(child.href);
              return (
                <Link
                  key={child.href}
                  to={child.href}
                  className={cn(
                    'whitespace-nowrap text-[13px] tracking-wide transition-colors border-b -mb-px py-2.5',
                    childActive
                      ? 'text-primary border-primary'
                      : 'text-muted-foreground border-transparent hover:text-foreground',
                  )}
                >
                  {t(child.labelKey)}
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      <div className="flex-1 flex items-start">
        <div className="flex-1 min-w-0 flex flex-col">
          <main id="main-content" className="flex-1 px-4 py-8 md:py-14">
            <div className="mx-auto max-w-6xl">
              <ProfileReviewAlert />

              {/* 编辑式页眉：金色发丝线 + kicker */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-1">
                  <span className="h-px w-8 bg-primary" />
                  <span className="text-[11px] tracking-[0.35em] uppercase text-primary font-medium">
                    Dashboard
                  </span>
                </div>
              </div>

              <AnimatePresence>
                <motion.div
                  key={pathname}
                  initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.99 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={prefersReducedMotion ? undefined : { opacity: 0, position: 'absolute' }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>

          <ConfigurableFooter variant="dashboard" config={footerConfig} />
        </div>

        {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      </div>
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 顶部文字导航                                                        */
/* ------------------------------------------------------------------ */

function LuxuryNav({
  pathname,
  navItems,
  isActive,
}: {
  pathname: string;
  navItems: ReturnType<typeof useNavigation>['navItems'];
  isActive: (href: string) => boolean;
}) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const t = useTranslations('nav');
  const tAuth = useTranslations('auth');
  const tUsers = useTranslations('users');
  const tCommon = useTranslations('common');
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations('guided');

  return (
    <header className="sticky top-0 z-40 border-b border-primary/25 bg-background/95 backdrop-blur-xl">
      <div className="mx-auto max-w-6xl flex h-16 items-center gap-4 px-4">
        <div className="md:hidden">
          <MobileSidebar />
        </div>

        <BrandLogo variant="auto" size="md" linkTo="/" />

        {/* 横向文字菜单 */}
        <nav className="hidden md:flex items-center gap-1 ml-4 flex-1 min-w-0 overflow-x-auto">
          {navItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'whitespace-nowrap px-3 py-2 text-[13px] tracking-[0.08em] uppercase transition-colors border-b-2 -mb-[2px]',
                  active
                    ? 'text-primary border-primary'
                    : 'text-muted-foreground border-transparent hover:text-foreground',
                )}
              >
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>

        <span className="flex-1 md:hidden" />

        <div className="flex items-center gap-1">
          {guidedEligible && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 tracking-wide"
              onClick={() => setUiMode('guided')}
              aria-label={tGuided('switchGuided')}
              title={tGuided('switchGuided')}
            >
              <Sparkles className="h-4 w-4" />
              <span className="ml-1 hidden lg:inline">{tGuided('switchGuided')}</span>
            </Button>
          )}
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={tCommon('toggleTheme')}
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-9 w-9"
            onClick={() => navigate('/dashboard/messages')}
            aria-label={tCommon('openMessages')}
          >
            <Mail className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
          <LuxuryUserMenu tAuth={tAuth} tUsers={tUsers} />
        </div>
      </div>
    </header>
  );
}

function LuxuryUserMenu({
  tAuth,
  tUsers,
}: {
  tAuth: ReturnType<typeof useTranslations>;
  tUsers: ReturnType<typeof useTranslations>;
}) {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { logout, isPending } = useLogout();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isPrivileged =
    user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger className="flex items-center gap-2 rounded-full p-0.5 pr-2 hover:bg-overlay/60 transition-colors cursor-pointer">
          <Avatar className="h-8 w-8 rounded-full border border-primary/40">
            <AvatarImage src="/avatar.png" />
            <AvatarFallback className="rounded-full text-[11px]">
              {user?.displayName?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground hidden sm:block" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium truncate">{user?.displayName || user?.email}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <RoleSwitcherMenuItems onSwitchSuccess={() => setMenuOpen(false)} />
          <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
            <PenLine className="h-4 w-4" />
            {tUsers('changeName')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="h-4 w-4" />
            {tUsers('changePassword')}
          </DropdownMenuItem>
          {isPrivileged && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="h-4 w-4" />
                {tAuth('logout.settings')}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} disabled={isPending} className="text-error-text">
            <LogOut className="h-4 w-4" />
            {tAuth('logout.title')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <ChangeNameDialog open={changeNameOpen} onOpenChange={setChangeNameOpen} />
    </>
  );
}
